//src/screens/OTPVerificationScreen.tsx
import React, { useState, useEffect, useRef } from "react";
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Alert,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { FlickOColors } from "../styles/color";
import { config } from "../../../config";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../../../types/navigation";
import { verificationService } from "../../../services/verification.service";
import LoadingSpinnerTrans from "../LoadingSpinner";
import { getResponsiveSize } from "../styles/globalStyles";
import { VerifyOtpResponse } from "../verification.types";
import { formatTime } from "../utils/formatters";
import { checkSubdomain, SubdomainCheck } from "../../../pwa/helpers";
import { fetchRestaurantIdByName } from "../../../pwa/linking.config";

type Props = StackScreenProps<RootStackParamList, "OTPVerification">;

const OTPVerificationScreen = ({ route, navigation }: Props) => {
  const { phoneNumber, successCallback } = route.params;
  const [loading, setLoading] = useState(false);
  const [otpDigits, setOtpDigits] = useState<string[]>(Array(6).fill(""));
  const [timer, setTimer] = useState(300);
  const [resendCount, setResendCount] = useState(1);
  const inputRefs = useRef<Array<TextInput | null>>([]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (timer > 0) {
      interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  const handleOtpChange = (text: string, index: number) => {
    const newOtpDigits = [...otpDigits];
    newOtpDigits[index] = text;
    setOtpDigits(newOtpDigits);

    if (text) {
      if (index < 5) inputRefs.current[index + 1]?.focus();
    } else {
      if (index > 0) inputRefs.current[index - 1]?.focus();
    }
  };

  // If newResendCount = 1, it setTimer 5^1 = 5.
  // If newResendCount = 2, it setTimer 5^2 = 25.
  // If newResendCount = 3, it setTimer 5^3 = 125.
  const updateResendCountAndTimer = () => {
    const newResendCount = resendCount + 1; // Increment resend count
    setResendCount(newResendCount); // Update resend count state
    setTimer(60 * Math.pow(5, newResendCount)); // Set new timer value
  };

  const handleResendOTP = () => {
    if (resendCount < 3) {
      updateResendCountAndTimer();
      Alert.alert("OTP Sent", "New OTP has been sent to your number.");
    } else {
      Alert.alert(
        "Max Resend Limit",
        "You have reached the maximum resend attempts."
      );
    }
  };

  const handleVerifyOTP = async () => {
    const otp = otpDigits.join("");
    try {
      setLoading(true);
      const response: VerifyOtpResponse = await verificationService.verifyOtp(
        route.params.verSessId,
        otp,
        phoneNumber
      );

      if (response.verification_status) {
        Alert.alert("Success", "Phone number verified successfully");
        await successCallback();
        const { isSubdomain, subdomain }: SubdomainCheck = checkSubdomain();
        console.log("isSubdomain, subdomain: ", isSubdomain, subdomain);
        if (isSubdomain && subdomain) {
          const restaurantId = await fetchRestaurantIdByName(subdomain);
          console.log("restaurantId:", restaurantId);

          navigation.replace("RestaurantMenu", {
            id: restaurantId,
            updatedAt: "",
            onEditPress: () => {},
          });
        } else {
          navigation.replace("HomeScreen");
        }
      } else {
        Alert.alert("Error", "Invalid OTP. Please try again.");
      }
    } catch (error: any) {
      Alert.alert(
        "Error",
        error.message || "Failed to verify OTP. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <SafeAreaView style={styles.modalOverlay}>
      {loading && <LoadingSpinnerTrans />}
      <View style={styles.otpModalContent}>
        <View style={styles.otpHeader}>
          <View>
            <TouchableOpacity
              onPress={() => navigation.goBack()}
              style={styles.backButton}
            >
              <Text style={{ fontSize: getResponsiveSize(40) }}>←</Text>
            </TouchableOpacity>
            <Text style={styles.otpTitle}>VERIFY DETAILS</Text>
            <Text style={styles.otpSubtitle}>OTP sent to {phoneNumber}</Text>
          </View>
        </View>

        <View style={styles.otpMainContent}>
          <Text style={styles.otpInputLabel}>ENTER OTP</Text>
          <View style={styles.otpInputContainer}>
            {otpDigits.map((digit, index) => (
              <View key={index} style={styles.otpDigitContainer}>
                <TextInput
                  ref={(ref) => {
                    inputRefs.current[index] = ref;
                  }}
                  style={styles.otpDigitInput}
                  keyboardType="numeric"
                  maxLength={1}
                  value={digit}
                  onChangeText={(text) => handleOtpChange(text, index)}
                />
                <View style={styles.otpDigitUnderline} />
              </View>
            ))}
          </View>

          <View style={styles.resendContainer}>
            <Text style={styles.defaultResendText}>
              {timer > 0
                ? `Didn't receive the OTP? Retry in ${formatTime(timer)}`
                : "Didn't receive the OTP?"}
            </Text>
            {timer === 0 && (
              <TouchableOpacity onPress={handleResendOTP}>
                <Text style={styles.retryNowText}>Retry now</Text>
              </TouchableOpacity>
            )}
          </View>

          <TouchableOpacity
            style={[
              styles.verifyButton,
              !otpDigits.every((d) => d !== "") && styles.verifyButtonDisabled,
            ]}
            onPress={handleVerifyOTP}
            disabled={!otpDigits.every((d) => d !== "")}
          >
            <Text style={styles.verifyButtonText}>VERIFY AND PROCEED</Text>
          </TouchableOpacity>
        </View>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: FlickOColors.gray.lightest,
    width: "100%",
    alignSelf: "center",
  },
  otpModalContent: {
    flex: 1,
    maxWidth: 480,
    alignSelf: "center",
    width: "100%",
  },
  otpHeader: {
    flexDirection: "row",
    padding: getResponsiveSize(16),
    backgroundColor: FlickOColors.background.dark,
  },
  backButton: {
    marginBottom: getResponsiveSize(10),
  },
  otpMainContent: {
    padding: getResponsiveSize(24),
  },
  otpTitle: {
    fontSize: getResponsiveSize(24),
    fontWeight: "bold",
    color: FlickOColors.text.primary,
    marginBottom: getResponsiveSize(8),
  },
  otpSubtitle: {
    fontSize: getResponsiveSize(14),
    color: FlickOColors.text.secondary,
    marginBottom: getResponsiveSize(32),
  },
  otpInputLabel: {
    fontSize: getResponsiveSize(14),
    color: FlickOColors.text.secondary,
    marginBottom: getResponsiveSize(16),
  },
  otpInputContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: getResponsiveSize(24),
  },
  otpDigitContainer: {
    width: getResponsiveSize(40),
  },
  otpDigitInput: {
    width: getResponsiveSize(40),
    height: getResponsiveSize(40),
    textAlign: "center",
    fontSize: getResponsiveSize(24),
    color: FlickOColors.text.primary,
  },
  otpDigitUnderline: {
    height: getResponsiveSize(1),
    backgroundColor: FlickOColors.gray.light,
    width: "100%",
  },
  resendContainer: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    marginTop: getResponsiveSize(24),
  },
  defaultResendText: {
    color: FlickOColors.text.secondary,
    fontSize: getResponsiveSize(14),
    marginRight: getResponsiveSize(5),
  },
  retryNowText: {
    color: FlickOColors.primary,
    fontSize: getResponsiveSize(14),
    fontWeight: "600",
    textDecorationLine: "underline",
  },
  retryTextInactive: {
    opacity: 0.5,
  },
  verifyButton: {
    backgroundColor: FlickOColors.primary,
    paddingVertical: getResponsiveSize(16),
    borderRadius: getResponsiveSize(8),
    marginTop: getResponsiveSize(32),
  },
  verifyButtonDisabled: {
    opacity: 0.6,
  },
  verifyButtonText: {
    color: FlickOColors.white,
    fontSize: getResponsiveSize(16),
    fontWeight: "600",
    textAlign: "center",
  },
});

export default OTPVerificationScreen;
